import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// MUI Core Components
import {
  Box, CircularProgress, Alert, Grid, Card, CardContent,
  CardMedia, Typography, RadioGroup, FormControlLabel, Radio, FormControl,
  Select, MenuItem, TextField, Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';

// MUI Icons for better UI
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HotelIcon from '@mui/icons-material/Hotel';
import HikingIcon from '@mui/icons-material/Hiking';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DateRangeIcon from '@mui/icons-material/DateRange';

// MUI X Date Pickers - CORRECTED: Using the free DatePicker component
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // <-- Use the free DatePicker

// Local Imports
import WizardLayout from '../../components/planner/WizardLayout';
import FinalDetailsForm from '../../components/planner/FinalDetailsForm';
import { fetchData } from '../../api/publicService';
import { submitInquiry } from '../../api/inquiryService';

// --- STYLING & CONSTANTS ---

// const STEP_TITLES = ['Choose Your Starting Point', 'Personalize Your Adventure', 'Confirm Your Details'];

const PALETTE = {
  stoneWhite: '#FCF9F3',
  peacockTeal: '#005B5C',
  saffronOrange: '#FCA510',
  textPrimary: '#1A2027',
  textSecondary: '#5F5F5F',
};

const StyledTourCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.07)',
  transition: 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
  },
}));

const CardImageOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  color: 'white',
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 60%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: '16px',
});

const PersonalizationBlock = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    height: '100%',
}));

const initialFormData = {
  baseTemplate: '',
  baseTemplateTitle: '',
  pacing: 'Balanced',
  accommodationStyle: 'Boutique',
  notes: '',
  tripDetails: {
    travelers: { adults: 1, children: 0 },
  },
  dateRange: [null, null], // [startDate, endDate]
  contactDetails: { name: '', email: '', phone: '' },
};


const CustomizeTourWizard = () => {
  // ... (All state and useEffect hooks remain the same)
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const STEP_TITLES = [
    t('wizard_step1_title'),
    t('wizard_step2_title'),
    t('wizard_step3_title')
  ];

  useEffect(() => {
      const loadTrips = async () => {
        try {
          setLoading(true);
          setError(null);
          const data = await fetchData('/trips', i18n.language);
          if (Array.isArray(data)) {
            setTrips(data);
          } else {
            setError(t('wizard_error_invalid_data'));
          }
        } catch (err) {
          setError(t('wizard_error_load_tours'));
        } finally {
          setLoading(false);
        }
      };
      loadTrips();
    }, [i18n.language, t]); 

  const handleNext = () => {
    if (currentStep === 3) {
      handleSubmit();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setCurrentStep((prev) => prev - 1);

  const handleSelectTrip = (trip) => {
    setFormData(prev => ({ ...prev, baseTemplate: trip._id, baseTemplateTitle: trip.title }));
    setCurrentStep(prev => prev + 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  // This handler now works with the two separate DatePicker components
  const handleDateChange = (newDateRange) => {
    setFormData(prev => ({...prev, dateRange: newDateRange}));
  };

  const handleFinalDetailsUpdate = (updatedData) => {
    setFormData(updatedData);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const [startDate, endDate] = formData.dateRange;
      let formattedDates = 'Not specified';
      if (startDate && endDate) {
        formattedDates = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
      }

      const payload = {
        inquiryType: 'Template Customization',
        contactDetails: formData.contactDetails,
        tripDetails: {
          ...formData.tripDetails,
          dates: formattedDates,
        },
        customizationDetails: {
          baseTemplate: formData.baseTemplate,
          notes: `Pacing: ${formData.pacing}. Accommodation: ${formData.accommodationStyle}. Other Requests: ${formData.notes}`,
        },
      };
      
      await submitInquiry(payload);
      navigate('/plan-your-trip/thank-you');
    } catch (err) {
      console.error("Error submitting inquiry:", err);
      setError('Submission failed. Please check your details and try again.');
    } finally {
      setSubmitting(false);
    }
  };


  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // ... (Step 1 rendering remains the same)
        if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 5}}><CircularProgress /></Box>;
        if (error) return <Alert severity="error">{error}</Alert>;
        return (
          <Grid container spacing={4}>
            {trips.map((trip) => (
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={trip._id}>
                <StyledTourCard onClick={() => handleSelectTrip(trip)}>
                    <Box sx={{ position: 'relative', height: '220px' }}>
                      <CardMedia
                        component="img"
                        height="220"
                        image={trip.images?.[0] || 'https://via.placeholder.com/400x220'}
                        alt={trip.title}
                      />
                      <CardImageOverlay>
                         <Typography variant="h6" component="div" fontWeight="bold">
                            {trip.title}
                         </Typography>
                      </CardImageOverlay>
                    </Box>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: PALETTE.textSecondary, mb: 1 }}>
                        <AccessTimeIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />
                        <Typography variant="body2">{trip.duration} Days</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{
                        display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {trip.summary}
                      </Typography>
                    </CardContent>
                </StyledTourCard>
              </Grid>
            ))}
          </Grid>
        );
      case 2:
        return (
          <Paper sx={{ p: {xs: 2, md: 4}, borderRadius: 4, background: '#fcfcfc' }}>
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 500, color: PALETTE.peacockTeal, textAlign: 'center' }}>
                          {t('wizard_personalize_title', { title: formData.baseTemplateTitle })}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 5 }}>
                          {t('wizard_personalize_subtitle')}
                        </Typography>
            <Grid container spacing={4}>
                {/* Travel Dates - REVISED SECTION */}
                <Grid item size={12}>
                   <PersonalizationBlock>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <DateRangeIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                          <Typography variant="h6" component="legend">{t('wizard_personalize_travel_dates')}</Typography>  </Box>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={2}>
                           <Grid item size={{ xs: 12, sm: 6 }}>
                                <DatePicker label={t('wizard_start_date_label')} value={formData.dateRange[0]} onChange={(newValue) => handleDateChange([newValue, formData.dateRange[1]])} sx={{ width: '100%' }} />
                           </Grid>
                           <Grid item size={{ xs: 12, sm: 6 }}>
                                <DatePicker label={t('wizard_end_date_label')} value={formData.dateRange[1]} onChange={(newValue) => handleDateChange([formData.dateRange[0], newValue])} minDate={formData.dateRange[0] || undefined} sx={{ width: '100%' }} />
                           </Grid>
                        </Grid>
                      </LocalizationProvider>
                   </PersonalizationBlock>
                </Grid>
                {/* Other personalization blocks remain the same */}
                <Grid item size={{ xs: 12, md: 6 }}>
                  <PersonalizationBlock>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <HikingIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                                                    <Typography variant="h6" component="legend">{t('wizard_personalize_pacing')}</Typography>
                      </Box>
                      <RadioGroup row name="pacing" value={formData.pacing} onChange={handleChange}>
                        <FormControlLabel value="Relaxed" control={<Radio />} label={t('wizard_pacing_relaxed')} />
                                                <FormControlLabel value="Balanced" control={<Radio />} label={t('wizard_pacing_balanced')} />
                                                <FormControlLabel value="Fast-Paced" control={<Radio />} label={t('wizard_pacing_fast')} />
                      </RadioGroup>
                  </PersonalizationBlock>
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                   <PersonalizationBlock>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <HotelIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                          <Typography variant="h6" component="legend">{t('wizard_personalize_accommodation')}</Typography>
                      </Box>
                      <FormControl fullWidth>
                          <Select name="accommodationStyle" value={formData.accommodationStyle} onChange={handleChange}>
                            <MenuItem value="Boutique">{t('wizard_accommodation_boutique')}</MenuItem>
                                                        <MenuItem value="Luxury">{t('wizard_accommodation_luxury')}</MenuItem>
                                                        <MenuItem value="Eco-lodges">{t('wizard_accommodation_eco')}</MenuItem>
                          </Select>
                      </FormControl>
                   </PersonalizationBlock>
                </Grid>
                <Grid item size={12}>
                  <PersonalizationBlock>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                         <EditNoteIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                                                   <Typography variant="h6" component="legend">{t('wizard_personalize_requests')}</Typography>
                      </Box>
                    <TextField fullWidth multiline rows={4} name="notes" value={formData.notes} onChange={handleChange} placeholder={t('wizard_requests_placeholder')} />
                                     
                  </PersonalizationBlock>
                </Grid>
            </Grid>
          </Paper>
        );
      case 3:
        return <FinalDetailsForm data={formData} onUpdate={handleFinalDetailsUpdate} />;
      default:
        return null;
    }
  };
  
  return (
    <WizardLayout
      step={currentStep} totalSteps={3} title="Customize Your Dream Tour"
      stepTitles={STEP_TITLES} onNext={handleNext} onBack={handleBack}
      isFirstStep={currentStep === 1} isLastStep={currentStep === 3}
      isSubmitting={submitting} submitText="Get My Personalized Quote"
      sx={{ mb:5 }}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {renderStepContent()}
    </WizardLayout>
  );
};

export default CustomizeTourWizard;